import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { invalidateProjectRelatedQueries } from '@/modules/common/utils/query-invalidation';

import { deleteProjects } from '../api/projects.api';

/**
 * Hook for bulk deleting projects.
 * Handles mutation, cache invalidation, and toast notifications.
 *
 * @param onSuccess - Optional callback to execute after successful deletion
 * @returns TanStack Query mutation for deleting projects
 */
export const useDeleteProjectsBulk = ({ onSuccess }: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('authenticated.projects');
  const { displayToastErrorFromApiWithFallback } = useApiError();

  return useMutation({
    mutationFn: (projectIds: number[]) => deleteProjects(projectIds),
    onSuccess: async (_, projectIds) => {
      // Invalidate all project related queries
      await invalidateProjectRelatedQueries(queryClient);

      displayToastSuccess(
        t('delete.deleteSuccess', {
          count: projectIds.length,
        })
      );

      if (onSuccess) {
        onSuccess();
      }
    },
    onError: error => {
      displayToastErrorFromApiWithFallback(error, 'delete.deleteError');
    },
  });
};
